/**
 * Input Component — Apex Design System v3.
 *
 * Five states: default, hover, focus, error, disabled.
 *
 * Premium details:
 * - Refined 0.5px border with warm tint
 * - Focus: brand-tinted background wash + luminous glow ring
 * - Hover: subtle border strengthening
 * - Error: danger border + fill + shake animation
 * - Clean label typography with proper spacing
 */

import { clsx } from "clsx";
import type { InputHTMLAttributes } from "react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

function Input({ label, error, className, id, ...props }: InputProps) {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-[13px] font-medium text-[var(--text-primary)]"
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        className={clsx(
          "block w-full h-[38px] rounded-[var(--radius-md)] px-3 text-sm",
          "bg-[var(--surface-base)]",
          "text-[var(--text-primary)]",
          "placeholder:text-[var(--text-tertiary)]",
          "transition-all duration-200 ease-[var(--ease-micro)]",
          "hover:border-[var(--border-strong)]",
          "focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:bg-[var(--color-interactive-primary)]/[0.04] focus-visible:border-[var(--border-focus)]",
          error
            ? "border border-[var(--text-danger)] bg-[var(--fill-danger)]/50 animate-shake"
            : "border-[0.5px] border-[var(--border-default)]",
          "disabled:pointer-events-none disabled:opacity-50 disabled:bg-[var(--surface-sunken)]",
          className,
        )}
        {...props}
      />
      {error && (
        <p className="text-xs text-[var(--text-danger)] mt-1">{error}</p>
      )}
    </div>
  );
}

export default Input;
